import { cx } from "@suse-o11y/class-variance";
import { format } from "@suse-o11y/date-time";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import React from "react";

import { Truncated } from "~/components/truncated";

import type { Api, LogSeverity } from "../../../../../../common/openapi";
import { Tooltip } from "../../../../../../components/react/tooltip/Tooltip";
import { logSeverityToColor } from "../../../../../../generic/log-severity";

import { LogSeverityDescription } from "./_log-severity-description";

const CONTAINER_COLORS = [
	cx("text-grey-800"),
	cx("text-purple-500"),
	cx("text-pink-600"),
	cx("text-teal-700"),
	cx("text-yellow-700"),
	cx("text-(--base-blue-600)"),
	cx("text-green-700"),
	cx("text-orange-600"),
	cx("text-graphite-600"),
	cx("text-purple-600"),
	cx("text-pink-700"),
	cx("text-teal-800"),
	cx("text-yellow-800"),
	cx("text-(--base-blue-700)"),
	cx("text-green-800"),
	cx("text-orange-700"),
];

export type CellContextWithExtraOptions<T> = CellContext<Api.KubernetesLogRecord, T> & {
	extraOptions?: {
		containerIndexes?: ReadonlyMap<string, number>;
		shouldWrapLines?: boolean;
	};
};

export const logsDrawerColumns: Array<ColumnDef<Api.KubernetesLogRecord>> = [
	{
		accessorKey: "severity",
		cell: (info: CellContext<Api.KubernetesLogRecord, LogSeverity>): React.ReactElement => {
			const severity = info.getValue();

			return (
				<Tooltip placement="right" title={<LogSeverityDescription severity={severity} />}>
					<div className="absolute top-0 h-full px-3">
						<div className="h-full w-[3px]" style={{ backgroundColor: logSeverityToColor(severity) }} />
					</div>
				</Tooltip>
			);
		},
		size: 27,
	},
	{
		accessorKey: "timestamp",
		cell: (info: CellContext<Api.KubernetesLogRecord, string>): React.ReactElement => {
			const time: string = info.getValue();
			return <p className="m-0">{format(new Date(time), "yyyy-MM-dd HH:mm:ss")}</p>;
		},
		size: 138,
	},
	{
		accessorKey: "containerName",
		size: 150,
		cell: (info: CellContextWithExtraOptions<string>): React.ReactElement => {
			const value: string = info.getValue();
			const index = info.extraOptions?.containerIndexes?.get(value) ?? 0;
			const cssColor = CONTAINER_COLORS[index % CONTAINER_COLORS.length];
			const shouldWrap: boolean | undefined = info.extraOptions?.shouldWrapLines;

			return (
				<div className={cx("w-[150px]", cssColor)}>
					{shouldWrap ? (
						<div className="wrap-break-word whitespace-normal">{value}</div>
					) : (
						<Truncated truncate="middle">{value}</Truncated>
					)}
				</div>
			);
		},
	},
	{
		accessorKey: "message",
		cell: (info: CellContextWithExtraOptions<string>): React.ReactElement => {
			const cellContent: string = info.getValue();
			const cellContentHighlights: Array<Api.KubernetesLogHighlight> = info.row.original.highlights;

			if (cellContentHighlights.length === 0) {
				return <div>{cellContent}</div>;
			}

			// Note: If the highlighting ever looks weird where it is highlighting more or less text
			//       Or it is highlighting over other words then it might be that the highlighted array is out of order
			//       Then we need to sort it based on the start index (Possible cause)

			// Split the main cell content into sections
			// - First we split out the part before the highlighting based on the last index we received
			//   What this means is the first split will be at 0 for the start
			//   The second split will start at where the previous highlighted split stopped
			//   You can almost see this as tail recursion on a string we a splitting
			// - Second we split out the part that needs to be highlighted, this will be the end of the first split
			//   upto the end of the highlighted value we received from the backend
			// - Third after we split these out and created the jsx elements we will go into the next reduce cycle which
			//   Will like mentioned at the start, start splitting the string from where the previous split stopped
			// Reduce Type: [number, JSX.Element[]]
			//   @Array[Param1]: number - The index where the previous split stopped
			//   @Array[Param2]: number - A Array of generated JSX elements based on the previous splits
			const highlightedContent = cellContentHighlights.reduce<[number, Array<React.ReactElement>]>(
				(acc: [number, Array<React.ReactElement>], value: Api.KubernetesLogHighlight, index: number) => {
					const [previousIndex, previousElements] = acc;

					// The text that should not be highlighted
					const prefixText = cellContent.substring(previousIndex, value.startIndex);

					// The text that should be highlighted
					const highlightedText = cellContent.substring(value.startIndex, value.endIndex + 1);

					return [
						// Increase the end index as the back end stop on the character and not after
						value.endIndex + 1,
						previousElements.concat([
							<span key={`prefix-part-${index}`}>{prefixText}</span>,
							<span
								className="font-bold text-grey-900"
								data-testid="highlighted-text"
								key={`highlighted-part-${index}`}
							>
								{highlightedText}
							</span>,
						]),
					];
				},
				// We start on a index of 0 to indicate the start of the string
				[0, []],
			);

			// Because we are only combining prefixes and highlighted text we will miss the very last part of the
			// string thus we need to add it back in after the loop cycle
			// We will use where the last highlighted part stopped as the start index
			const suffixText = cellContent.substring(highlightedContent[0]);

			return (
				<>
					{highlightedContent[1]}
					<span>{suffixText}</span>
				</>
			);
		},
		meta: { enableWrap: true },
	},
];
